import { useState, useCallback, useMemo } from "react";
import { DropResult } from "react-beautiful-dnd";
import { Content } from "@/types";
import { MenuSection } from "@/types/menus";

// Droppable IDs
export const CATALOG_DROPPABLE = "catalog-products";
export const SECTION_LIST_DROPPABLE = "section-list";

interface UseCatalogEditorProps {
  initialStructure?: MenuSection[];
  allContent: Content[];
}

const useCatalogEditor = ({ initialStructure = [], allContent }: UseCatalogEditorProps) => {
  const [structure, setStructure] = useState<MenuSection[]>(initialStructure);
  const [searchQuery, setSearchQuery] = useState("");

  // ─── Filtered catalog products (left column) ───────────────────────────────
  const filteredProducts = useMemo(() => {
    const assignedIds = new Set<string>();
    structure.forEach((section) => {
      section.products?.forEach((id) => assignedIds.add(id));
    });

    const q = searchQuery.toLowerCase().trim();
    const unassigned = allContent.filter((c) => !assignedIds.has(c.id));

    if (!q) return unassigned;
    return unassigned.filter((c) => c.title?.toLowerCase().includes(q));
  }, [allContent, searchQuery, structure]);

  // ─── Section actions ────────────────────────────────────────────────────────
  const addSection = useCallback(() => {
    setStructure((prev) => [
      ...prev,
      {
        category: `section-${Date.now()}`,
        categoryTitle: "Nueva Sección",
        isVisible: true,
        columns: 1,
        template: "default-filled",
        products: [],
      },
    ]);
  }, []);

  const removeSection = useCallback((sectionIdx: number) => {
    setStructure((prev) => prev.filter((_, i) => i !== sectionIdx));
  }, []);

  const updateSection = useCallback((sectionIdx: number, patch: Partial<MenuSection>) => {
    setStructure((prev) =>
      prev.map((s, i) => (i === sectionIdx ? { ...s, ...patch } : s))
    );
  }, []);

  // Remove a product from a section
  const removeProductFromSection = useCallback((sectionIdx: number, productId: string) => {
    setStructure((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        return { ...s, products: (s.products ?? []).filter((id) => id !== productId) };
      })
    );
  }, []);

  // ─── Drag-and-drop logic ────────────────────────────────────────────────────
  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result;
      if (!destination) return;

      const srcId = source.droppableId;
      const dstId = destination.droppableId;

      // ── Reorder sections ──────────────────────────────────────────────────
      if (srcId === SECTION_LIST_DROPPABLE && dstId === SECTION_LIST_DROPPABLE) {
        setStructure((prev) => {
          const next = [...prev];
          const [moved] = next.splice(source.index, 1);
          next.splice(destination.index, 0, moved);
          return next;
        });
        return;
      }

      // Find if source/destination are sections by category ID
      const dstSectionIdx = structure.findIndex((s) => s.category === dstId);
      const srcSectionIdx = structure.findIndex((s) => s.category === srcId);

      // ── Drop product back from a section into the catalog ──────────────────
      if (dstId === CATALOG_DROPPABLE && srcSectionIdx !== -1) {
        const realProductId = draggableId.replace(/^section-product-\d+-/, "");
        setStructure((prev) =>
          prev.map((s, i) => {
            if (i !== srcSectionIdx) return s;
            return { ...s, products: (s.products ?? []).filter((id) => id !== realProductId) };
          })
        );
        return;
      }

      // ── Drop product from catalog into a section ───────────────────────────
      if (srcId === CATALOG_DROPPABLE && dstSectionIdx !== -1) {
        setStructure((prev) =>
          prev.map((s, i) => {
            if (i !== dstSectionIdx) return s;
            const existing = s.products ?? [];
            // Avoid duplicates
            if (existing.includes(draggableId)) return s;
            const next = [...existing];
            next.splice(destination.index, 0, draggableId);
            return { ...s, products: next };
          })
        );
        return;
      }

      // ── Reorder products within a section ─────────────────────────────────
      if (srcSectionIdx !== -1 && dstSectionIdx !== -1 && srcId === dstId) {
        setStructure((prev) =>
          prev.map((s, i) => {
            if (i !== srcSectionIdx) return s;
            const next = [...(s.products ?? [])];
            const [item] = next.splice(source.index, 1);
            next.splice(destination.index, 0, item);
            return { ...s, products: next };
          })
        );
        return;
      }

      // ── Move product between sections ─────────────────────────────────────
      if (srcSectionIdx !== -1 && dstSectionIdx !== -1) {
        setStructure((prev) => {
          const next = prev.map((s) => ({ ...s, products: [...(s.products ?? [])] }));
          const srcProducts = next[srcSectionIdx].products!;
          const dstProducts = next[dstSectionIdx].products!;
          const [item] = srcProducts.splice(source.index, 1);
          // Avoid duplicates in destination
          if (!dstProducts.includes(item)) {
            dstProducts.splice(destination.index, 0, item);
          }
          return next;
        });
      }
    },
    [structure]
  );

  return {
    structure,
    setStructure,
    filteredProducts,
    searchQuery,
    setSearchQuery,
    addSection,
    removeSection,
    updateSection,
    removeProductFromSection,
    onDragEnd,
  };
};

export default useCatalogEditor;
